import { apiSlice } from "../../../features/api/apiSlice";

/* ================= TYPES ================= */

export type PermissionLite = {
  id: number;
  name: string;
  action: string;
};

export type PermissionsByModule = Record<string, PermissionLite[]>;

export type Permission = {
  id: number;
  name: string;
  guard_name: string;
};

export type Role = {
  id: number;
  name: string;
  guard_name: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type RoleDetails = Role & {
  permissions: Permission[];
};

function pick(res: any) {
  console.log("this i s he response", res);
  return res?.data?.data ?? res?.data ?? res;
}

/* ================= API ================= */

export const accessApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ---------- ROLES (GENERAL) ---------- */

    getRoles: builder.query<Role[], void>({
      query: () => "/access/roles",
      transformResponse: (res: any) => pick(res) || [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({ type: "Roles" as const, id: r.id })),
              { type: "Roles" as const, id: "LIST" },
            ]
          : [{ type: "Roles" as const, id: "LIST" }],
    }),

    createRole: builder.mutation<Role, { name: string }>({
      query: (body) => ({
        url: "/access/roles",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Roles" as const, id: "LIST" }],
    }),

    deleteRole: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/access/roles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Roles" as const, id: "LIST" }],
    }),

    updateRole: builder.mutation<RoleDetails, { id: number; name: string }>({
      query: ({ id, ...body }) => ({
        url: `/access/roles/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "Roles" as const, id },
        { type: "Roles" as const, id: "LIST" },
      ],
    }),

    getRoleDetails: builder.query<RoleDetails, number>({
      query: (id) => `/access/roles/${id}`,
      transformResponse: (res: any) => pick(res),
      providesTags: (_r, _e, id) => [{ type: "Roles" as const, id }],
    }),

    /* ---------- PERMISSIONS ---------- */

    getAllPermissions: builder.query<PermissionsByModule, void>({
      query: () => "/access/permissions",
      transformResponse: (res: any) => pick(res) || {},
    }),

    /**
     * ✅ NEW preferred mutation: uses roleId
     * PUT /access/roles/:roleId/permissions
     * { permissions: ["members.view"] }
     */
    syncRolePermissions: builder.mutation<
      RoleDetails,
      { roleId: number; permissions: string[] }
    >({
      query: ({ roleId, permissions }) => ({
        url: `/access/roles/${roleId}/permissions`,
        method: "POST",
        body: { permissions },
      }),
      invalidatesTags: (_r, _e, { roleId }) => [
        { type: "Roles" as const, id: roleId },
      ],
    }),

    /**
     * ✅ NEW preferred revoke mutation: uses roleId object
     */
    revokeRolePermissions: builder.mutation<any, { roleId: number }>({
      query: ({ roleId }) => ({
        url: `access/roles/${roleId}/permissions/revoke`,
        method: "GET",
      }),
      invalidatesTags: (_r, _e, { roleId }) => [
        { type: "Roles" as const, id: roleId },
      ],
    }),

    /* ---------- ✅ BACKWARD COMPAT ALIASES (DO NOT REMOVE) ---------- */

    /**
     * Old contract used: { id, permissions }
     * Keep it so RoleDetailsPage / DesignationDetailsPage old code still works.
     */
    updateRolePermissions: builder.mutation<
      RoleDetails,
      { id: number; permissions: string[] }
    >({
      query: ({ id, permissions }) => ({
        url: `/access/roles/${id}/permissions`,
        method: "POST", // consistent with controller update
        body: { permissions },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Roles" as const, id }],
    }),

    /**
     * Old hook name expected: revokeAllRolePermissions(id)
     */
    revokeAllRolePermissions: builder.mutation<any, number>({
      query: (id) => ({
        url: `/access/roles/${id}/permissions/revoke`,
        method: "GET",
      }),
      invalidatesTags: (_r, _e, id) => [{ type: "Roles" as const, id }],
    }),
  }),
  overrideExisting: false,
});

/* ================= EXPORTS ================= */

export const {
  useGetRolesQuery,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useUpdateRoleMutation,
  useGetRoleDetailsQuery,
  useGetAllPermissionsQuery,

  // new
  useSyncRolePermissionsMutation,
  useRevokeRolePermissionsMutation,

  // aliases (old pages)
  useUpdateRolePermissionsMutation,
  useRevokeAllRolePermissionsMutation,
} = accessApi;
